import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useRouter } from 'next/router'
import dayjs from 'dayjs'
import { Button, Input, message, Popconfirm, Spin } from 'antd'
import { get, post, httpDelete } from 'utils/request'
import useLogin from '@/hooks/swr/useLogin'

type Comment = {
  id: string
  content: string
  parent_id?: string | null
  user_id?: string
  is_official?: boolean
  author: string
  avatar?: string
  created_at: string
}

const MAX_LENGTH = 1000

/**
 * 资讯评论区。一层盖楼：顶层评论 + 其下的回复，不做无限嵌套（嵌套超过两层在手机上没法读）。
 * 评论列表公开可读，发表需登录；作者本人与管理员可删除。
 */
const Comments: React.FC<{ postId: string }> = ({ postId }) => {
  const router = useRouter()
  const { isLogin, current_user } = useLogin()
  const [list, setList] = useState<Comment[]>([])
  const [loading, setLoading] = useState(true)
  const [text, setText] = useState('')
  const [replyTo, setReplyTo] = useState<Comment | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const load = useCallback(() => {
    get(`users/posts/${postId}/comments`).then((res: any) => {
      if (res?.ok) setList(res.comments ?? [])
      setLoading(false)
    })
  }, [postId])

  useEffect(load, [load])

  // 顶层评论 + 其回复，一次遍历分好组
  const threads = useMemo(() => {
    const roots = list.filter((c) => !c.parent_id)
    const byParent = new Map<string, Comment[]>()
    list.forEach((c) => {
      if (!c.parent_id) return
      byParent.set(c.parent_id, [...(byParent.get(c.parent_id) ?? []), c])
    })
    return roots.map((r) => ({ root: r, replies: byParent.get(r.id) ?? [] }))
  }, [list])

  const submit = async () => {
    const content = text.trim()
    if (!content) return
    setSubmitting(true)
    const res: any = await post(`users/posts/${postId}/comments`, { content, parent_id: replyTo?.id })
    setSubmitting(false)
    if (res?.ok) {
      setText('')
      setReplyTo(null)
      load()
    } else {
      message.error(res?.message || '发表失败')
    }
  }

  const remove = async (id: string) => {
    const res: any = await httpDelete(`users/posts/${postId}/comments/${id}`, {})
    if (res?.ok) load()
    else message.error(res?.message || '删除失败')
  }

  const canDelete = (c: Comment) => current_user?.id === c.user_id || current_user?.is_gd_admin

  const renderOne = (c: Comment, isReply = false) => (
    <div key={c.id} className={isReply ? 'mt-3 border-l-2 border-solid border-gray-100 pl-3' : ''}>
      <div className="flex items-baseline gap-2">
        <span className={`text-sm font-medium ${c.is_official ? 'text-[#DB3142]' : 'text-gray-800'}`}>{c.author}</span>
        {c.is_official && <span className="rounded bg-[#DB3142]/10 px-1.5 py-0.5 text-xs text-[#DB3142]">官方</span>}
        <span className="text-xs text-gray-400">{dayjs(c.created_at).format('MM-DD HH:mm')}</span>
      </div>
      <p className="mb-0 mt-1 whitespace-pre-wrap text-sm leading-relaxed text-gray-700">{c.content}</p>
      <div className="mt-1 flex gap-3 text-xs">
        <button type="button" className="cursor-pointer border-0 bg-transparent p-0 text-gray-400 hover:text-gray-600" onClick={() => setReplyTo(c)}>
          回复
        </button>
        {canDelete(c) && (
          <Popconfirm title="确认删除这条评论？" onConfirm={() => remove(c.id)} okText="删除" cancelText="取消">
            <button type="button" className="cursor-pointer border-0 bg-transparent p-0 text-gray-400 hover:text-[#DB3142]">
              删除
            </button>
          </Popconfirm>
        )}
      </div>
    </div>
  )

  return (
    <div className="mt-10 border-t border-x-0 border-b-0 border-solid border-gray-200 pt-6">
      <h3 className="mb-4 text-lg font-semibold text-gray-900">评论 {list.length > 0 ? `(${list.length})` : ''}</h3>

      {isLogin ? (
        <div className="mb-6 rounded-2xl bg-gray-50 p-4">
          {replyTo && (
            <div className="mb-2 flex items-center gap-2 text-xs text-gray-500">
              <span>回复 @{replyTo.author}</span>
              <button type="button" className="cursor-pointer border-0 bg-transparent p-0 text-gray-400 underline" onClick={() => setReplyTo(null)}>
                取消
              </button>
            </div>
          )}
          <Input.TextArea
            rows={3}
            value={text}
            maxLength={MAX_LENGTH}
            showCount
            placeholder="说点什么吧，建议、疑问或者你的办赛经验都欢迎"
            onChange={(e) => setText(e.target.value)}
          />
          <div className="mt-3 text-right">
            <Button
              type="primary"
              loading={submitting}
              disabled={!text.trim()}
              onClick={submit}
              className="border-[#DB3142] bg-[#DB3142] hover:bg-[#b91c1c]"
            >
              发表
            </Button>
          </div>
        </div>
      ) : (
        <div className="mb-6 rounded-2xl bg-gray-50 p-4 text-sm text-gray-500">
          <button
            type="button"
            className="cursor-pointer border-0 bg-transparent p-0 text-[#DB3142] underline"
            onClick={() => router.push({ pathname: '/login', query: { redirect: router.asPath } })}
          >
            登录
          </button>
          后可参与评论。
        </div>
      )}

      {loading ? (
        <div className="py-6 text-center">
          <Spin />
        </div>
      ) : threads.length === 0 ? (
        <p className="py-4 text-sm text-gray-400">还没有评论，来做第一个。</p>
      ) : (
        <div className="space-y-5">
          {threads.map(({ root, replies }) => (
            <div key={root.id} className="rounded-2xl bg-white p-4 ring-1 ring-black/5">
              {renderOne(root)}
              {replies.map((r) => renderOne(r, true))}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

export default Comments
